import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';

@Component({
    selector: 'material-button',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    standalone: false,
})
export class MaterialButtonComponent implements OnChanges, AfterViewInit {
    @ViewChild('unNamedMatMenuTrigger', { read: MatMenuTrigger, static: false }) unNamedMatMenuTrigger: MatMenuTrigger;

    @Input() text = '';
    @Input() description = '';
    @Input() color: 'primary' | 'danger' | 'warning' | 'default' | 'primary-lite' = 'default';
    @Input() buttonType: 'raised' | 'flat' | 'stroked' | 'icon' | 'fab' | 'mini-fab' = 'raised';
    @Input() size: 'small' | 'medium' | 'large' = 'medium';
    @Input() loading = false;
    @Input() tabIndex: number;
    @Input() loadingText = 'Loading...';
    @Input() disabled = false;
    @Input() hidden = false;
    @Input() isToggled = false;
    @Input() classList: string[] = [];
    @Input() prefixIconClassList: string[] = [];
    @Input() suffixIconClassList: string[] = [];
    @Input() prefixImgClassList: string[] = [];
    @Input() suffixImgClassList: string[] = [];
    @Input() labelClassList: string[] = [];
    @Input() textClassList: string[] = [];
    @Input() titleText = '';
    @Input() prefixIcon = '';
    @Input() suffixIcon = '';
    @Input() prefixIconType: 'material' | 'image' | 'svg' = 'material';
    @Input() prefixIconAppearance: 'outlined' | 'filled' = 'outlined';
    @Input() suffixIconType: 'material' | 'image' | 'svg' = 'material';
    @Input() suffixIconAppearance: 'outlined' | 'filled' = 'outlined';
    @Input() prefixImgSrc = '';
    @Input() suffixImgSrc = '';
    @Input() prefixSvgIcon = '';
    @Input() suffixSvgIcon = '';
    @Input() prefixImgAlt = '';
    @Input() suffixImgAlt = '';
    @Input() matMenu: MatMenu;
    @Input() hideRipple = false;
    @Input() buttonPurpose: 'ROUTER_LINK' | 'MENU_TRIGGER';
    @Input() routerLink: any[] | string = '';
    @Input() queryParams: { [k: string]: any } | null = null;

    @Output() buttonClicked = new EventEmitter<MouseEvent>();
    @Output() menuTriggerElementEmit = new EventEmitter<MatMenuTrigger>();

    computedClassList: string[] = [];
    displayText = '';
    displayDescription = '';
    prefixIconClasses = '';
    suffixIconClasses = '';
    perfixImgClasses = '';
    suffixImgClasses = '';

    ngOnChanges(_changes: SimpleChanges): void {
        this.updateClassList();
        this.updateDisplayProperties();
    }

    private updateClassList(): void {
        const classes = ['mdc-button'];

        // Add button type class
        switch (this.buttonType) {
            case 'raised':
                classes.push('mat-mdc-raised-button');
                break;
            case 'flat':
                classes.push('mat-mdc-button', 'mat-mdc-flat-button');
                break;
            case 'stroked':
                classes.push('mat-mdc-outlined-button');
                break;
            case 'icon':
                classes.push('mat-mdc-icon-button');
                break;
            case 'fab':
                classes.push('mat-mdc-fab');
                break;
            case 'mini-fab':
                classes.push('mat-mdc-mini-fab');
                break;
        }

        // Add color class
        if (this.color !== 'default') {
            classes.push(this.color);
        }

        // Add size class
        classes.push(this.size);

        // Add state classes
        if (this.loading) classes.push('loading');
        if (this.disabled) classes.push('disabled');
        if (this.isToggled) classes.push('toggled');

        // Add custom classes
        if (this.classList && this.classList.length > 0) {
            classes.push(...this.classList);
        }

        this.computedClassList = classes.filter(value => value !== undefined && value !== null && value !== '');
        if (this.prefixIconClassList) {
            this.prefixIconClassList = this.prefixIconClassList.filter(value => value !== undefined && value !== null && value !== '');
        }
        if (this.suffixIconClassList) {
            this.suffixIconClassList = this.suffixIconClassList.filter(value => value !== undefined && value !== null && value !== '');
        }
        if (this.labelClassList) {
            this.labelClassList = this.labelClassList.filter(value => value !== undefined && value !== null && value !== '');
        }
        if (this.textClassList) {
            this.textClassList = this.textClassList.filter(value => value !== undefined && value !== null && value !== '');
        }
        if (this.prefixImgClassList) {
            this.prefixImgClassList = this.prefixImgClassList.filter(value => value !== undefined && value !== null && value !== '');
        }
        if (this.suffixIconClassList) {
            this.suffixIconClassList = this.suffixIconClassList.filter(value => value !== undefined && value !== null && value !== '');
        }
    }

    private updateDisplayProperties(): void {
        this.displayText = this.loading ? this.loadingText : this.text;
        this.displayDescription = this.loading ? this.loadingText : '(' + this.description + ')';

        // Build prefix icon classes string
        const prefixClasses = ['material-symbols-outlined', this.prefixIconAppearance, 'mdc-button__icon'];
        if (this.prefixIconClassList && this.prefixIconClassList.length > 0) {
            prefixClasses.push(...this.prefixIconClassList);
        }
        this.prefixIconClasses = prefixClasses.join(' ');

        // Build suffix icon classes string
        const suffixClasses = ['material-symbols-outlined', this.prefixIconAppearance, 'mdc-button__icon'];
        if (this.suffixIconClassList && this.suffixIconClassList.length > 0) {
            suffixClasses.push(...this.suffixIconClassList);
            if (this.suffixIcon === 'drag_handle') {
                suffixClasses.push('cursor-move');
            }
        }
        this.suffixIconClasses = suffixClasses.join(' ');

        // Build prefix img classes string
        const prefixImgClasses = ['mdc-button__icon'];
        if (this.prefixImgClassList && this.prefixImgClassList.length > 0) {
            prefixImgClasses.push(...this.prefixImgClassList);
        }
        this.perfixImgClasses = prefixImgClasses.join(' ');

        // Build suffix img classes string
        const suffixImgClasses = ['mdc-button__icon'];
        if (this.suffixImgClassList && this.suffixImgClassList.length > 0) {
            suffixImgClasses.push(...this.suffixImgClassList);
        }
        this.suffixImgClasses = suffixImgClasses.join(' ');
    }

    onButtonClick(event: MouseEvent): void {
        if (this.loading || this.disabled) {
            return;
        }
        this.buttonClicked.emit(event);
    }

    ngAfterViewInit(): void {
        if (this.buttonPurpose === 'MENU_TRIGGER' && this.unNamedMatMenuTrigger) {
            this.menuTriggerElementEmit.emit(this.unNamedMatMenuTrigger);
        }
        this.updateDisplayProperties();
    }
}
